import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import config from '../config';
import { getOrganizationContext, setOrganizationContext } from '../orgContext';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminFilterBar, AdminPageHeader } from './components/AdminMasterDetail';
import { useAdminApi } from './hooks/useAdminApi';
import AdminAiAssistantModal from './components/AdminAiAssistantModal';

interface CategoryLookupItem {
  id: number;
  name: string;
  label?: string;
  parentCategoryId?: number | null;
  releaseVersion?: string | null;
  sortOrder?: number;
}

interface QuestionItem {
  id: number;
  categoryId: number;
  approvalStatus?: string | null;
  questionText: string;
  questionPrefix?: string | null;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson?: string | null;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA?: string | null;
  optionB?: string | null;
  optionC?: string | null;
  optionD?: string | null;
  optionE?: string | null;
  correctOption?: string | null;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex?: string | null;
  questionForOpenAI?: string | null;
  promptKey?: string | null;
  promptVersion?: string | null;
  promptModel?: string | null;
  promptResponseMode?: string | null;
  explanationGeneratedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface QuestionFormData {
  categoryId: number | null;
  approvalStatus: string;
  questionText: string;
  questionPrefix: string;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson: string;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  optionE: string;
  correctOption: string;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex: string;
  questionForOpenAI: string;
  promptKey: string;
  promptVersion: string;
  promptModel: string;
  promptResponseMode: string;
}

interface UserOrganizationItem {
  organizationId: number;
  organizationSlug: string;
  organizationName?: string;
  isDefault?: boolean;
}

interface CourseLeafOption {
  id: number;
  name: string;
  pathLabel: string;
}

interface BulkQuestionGenerationMeta {
  promptKey?: string;
  promptVersion?: string;
  model?: string;
}

interface BulkStatusUpdateResponse {
  requestedCount: number;
  updatedCount: number;
  updatedIds: number[];
  skippedIds: number[];
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const buildEmptyForm = (categoryId: number | null = null): QuestionFormData => ({
  categoryId,
  approvalStatus: 'Approved',
  questionText: '',
  questionPrefix: '',
  answerText: '',
  explanation: '',
  answerExplanation: '',
  answerExplanationJson: '',
  hint: '',
  difficultyLevel: 1,
  isActive: true,
  source: '',
  imageUrl: '',
  optionA: '',
  optionB: '',
  optionC: '',
  optionD: '',
  optionE: '',
  correctOption: '',
  isMultipleChoice: true,
  questionLatex: '',
  answerLatex: '',
  graphLatex: '',
  questionForOpenAI: '',
  promptKey: '',
  promptVersion: '',
  promptModel: '',
  promptResponseMode: ''
});

const normalizeText = (value?: string | null): string => (value || '').trim();

const blurActiveElement = (): void => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const normalizeApprovalStatus = (value?: string | null): string => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return 'Approved';
  }

  if (normalized.toLowerCase() === 'pending review') {
    return 'Pending Review';
  }

  if (normalized.toLowerCase() === 'rejected') {
    return 'Rejected';
  }

  return 'Approved';
};

const normalizeCorrectOption = (value?: string | null): string => {
  const text = normalizeText(value).toUpperCase();
  if (!text) {
    return '';
  }

  const direct = text.match(/^[A-E]$/);
  if (direct) {
    return direct[0];
  }

  const optionPattern = text.match(/OPTION\s*([A-E])|^([A-E])[).:\-\s]/);
  if (optionPattern) {
    return optionPattern[1] || optionPattern[2] || '';
  }

  return '';
};

const getProvidedOptionLetters = (draft: QuestionBulkDraft): string[] => {
  const options: Array<{ letter: string; value: string }> = [
    { letter: 'A', value: normalizeText(draft.optionA) },
    { letter: 'B', value: normalizeText(draft.optionB) },
    { letter: 'C', value: normalizeText(draft.optionC) },
    { letter: 'D', value: normalizeText(draft.optionD) },
    { letter: 'E', value: normalizeText(draft.optionE) },
  ];

  return options.filter((option) => option.value.length > 0).map((option) => option.letter);
};

const sanitizeBulkDraft = (draft: QuestionBulkDraft): QuestionBulkDraft => {
  const normalized = {
    ...draft,
    questionText: normalizeText(draft.questionText),
    questionPrefix: normalizeText(draft.questionPrefix),
    answerText: normalizeText(draft.answerText),
    explanation: normalizeText(draft.explanation),
    answerExplanation: normalizeText(draft.answerExplanation),
    answerExplanationJson: normalizeText(draft.answerExplanationJson),
    hint: normalizeText(draft.hint),
    source: normalizeText(draft.source),
    imageUrl: normalizeText(draft.imageUrl),
    optionA: normalizeText(draft.optionA),
    optionB: normalizeText(draft.optionB),
    optionC: normalizeText(draft.optionC),
    optionD: normalizeText(draft.optionD),
    optionE: normalizeText(draft.optionE),
    questionLatex: normalizeText(draft.questionLatex),
    answerLatex: normalizeText(draft.answerLatex),
    graphLatex: normalizeText(draft.graphLatex),
    questionForOpenAI: normalizeText(draft.questionForOpenAI),
    promptKey: normalizeText(draft.promptKey),
    promptVersion: normalizeText(draft.promptVersion),
    promptModel: normalizeText(draft.promptModel),
    promptResponseMode: normalizeText(draft.promptResponseMode),
    correctOption: normalizeCorrectOption(draft.correctOption),
  };

  if (!normalized.correctOption) {
    const optionsByLetter: Array<{ letter: string; value: string }> = [
      { letter: 'A', value: normalized.optionA },
      { letter: 'B', value: normalized.optionB },
      { letter: 'C', value: normalized.optionC },
      { letter: 'D', value: normalized.optionD },
      { letter: 'E', value: normalized.optionE },
    ];

    const matchingOption = optionsByLetter.find(
      (option) => option.value.length > 0 && option.value.toLowerCase() === normalizeText(draft.correctOption).toLowerCase()
    );

    if (matchingOption) {
      normalized.correctOption = matchingOption.letter;
    }
  }

  return ensureDraftHasMcqOptions(normalized);
};

const getBulkDraftValidationError = (draft: QuestionBulkDraft): string | null => {
  if (!draft.questionText) {
    return 'Question text is missing.';
  }

  if (!draft.isMultipleChoice) {
    return null;
  }

  const providedLetters = getProvidedOptionLetters(draft);
  if (providedLetters.length < 2) {
    return 'At least two options are required for multiple-choice questions.';
  }

  if (!draft.correctOption || !providedLetters.includes(draft.correctOption)) {
    return `Correct option must be one of: ${providedLetters.join(', ')}.`;
  }

  return null;
};

const parseBulkQuestionDrafts = (generatedText: string, meta?: BulkQuestionGenerationMeta): QuestionBulkDraft[] => {
  const parsed = JSON.parse(generatedText) as unknown;
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.map((item, index) => {
    const draft = (item ?? {}) as Record<string, unknown>;
    const approvalStatus = normalizeApprovalStatus(String(draft.approvalStatus ?? 'Pending Review'));
    const isActive = String(draft.isActive ?? 'false').toLowerCase() === 'true';
    const parsedDraft: QuestionBulkDraft = {
      id: index + 1,
      categoryId: Number(draft.categoryId) || 0,
      approvalStatus,
      optionsAutoGenerated: false,
      questionText: String(draft.questionText ?? '').trim(),
      questionPrefix: String(draft.questionPrefix ?? '').trim(),
      answerText: String(draft.answerText ?? '').trim(),
      explanation: String(draft.explanation ?? '').trim(),
      answerExplanation: String(draft.answerExplanation ?? '').trim(),
      answerExplanationJson: String(draft.answerExplanationJson ?? '').trim(),
      hint: String(draft.hint ?? '').trim(),
      difficultyLevel: Math.min(3, Math.max(1, Number(draft.difficultyLevel) || 1)),
      isActive,
      source: String(draft.source ?? '').trim(),
      imageUrl: String(draft.imageUrl ?? '').trim(),
      optionA: String(draft.optionA ?? '').trim(),
      optionB: String(draft.optionB ?? '').trim(),
      optionC: String(draft.optionC ?? '').trim(),
      optionD: String(draft.optionD ?? '').trim(),
      optionE: String(draft.optionE ?? '').trim(),
      correctOption: String(draft.correctOption ?? '').trim().toUpperCase(),
      isMultipleChoice: String(draft.isMultipleChoice ?? 'true').toLowerCase() !== 'false',
      questionLatex: String(draft.questionLatex ?? '').trim(),
      answerLatex: String(draft.answerLatex ?? '').trim(),
      graphLatex: String(draft.graphLatex ?? '').trim(),
      questionForOpenAI: String(draft.questionForOpenAI ?? '').trim(),
      promptKey: String(draft.promptKey ?? meta?.promptKey ?? 'admin-questions-bulk-ai').trim(),
      promptVersion: String(draft.promptVersion ?? meta?.promptVersion ?? '').trim(),
      promptModel: String(draft.promptModel ?? meta?.model ?? '').trim(),
      promptResponseMode: String(draft.promptResponseMode ?? 'json').trim(),
    };

    return ensureDraftHasMcqOptions(parsedDraft);
  });
};

interface QuestionBulkDraft {
  id: number;
  categoryId: number;
  approvalStatus: string;
  optionsAutoGenerated?: boolean;
  questionText: string;
  questionPrefix: string;
  answerText: string;
  explanation: string;
  answerExplanation: string;
  answerExplanationJson: string;
  hint: string;
  difficultyLevel: number;
  isActive: boolean;
  source: string;
  imageUrl: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  optionE: string;
  correctOption: string;
  isMultipleChoice: boolean;
  questionLatex: string;
  answerLatex: string;
  graphLatex: string;
  questionForOpenAI: string;
  promptKey: string;
  promptVersion: string;
  promptModel: string;
  promptResponseMode: string;
}

const generateDefaultOptionsFromAnswer = (answerText: string): string[] => {
  const answer = normalizeText(answerText);
  if (!answer) {
    return [
      'Choice A',
      'Choice B',
      'Choice C',
      'Choice D',
      'Choice E',
    ];
  }

  const numericMatch = answer.match(/-?\d+(?:\.\d+)?/);
  if (numericMatch) {
    const numericValue = Number(numericMatch[0]);
    if (!Number.isNaN(numericValue)) {
      const unit = normalizeText(answer.replace(numericMatch[0], ''));
      const withUnit = (value: number) => {
        const formatted = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
        return unit ? `${formatted} ${unit}` : formatted;
      };

      return [
        answer,
        withUnit(numericValue + 1),
        withUnit(numericValue - 1),
        withUnit(numericValue * 2),
        withUnit(numericValue / 2),
      ];
    }
  }

  return [
    answer,
    `Approximately ${answer}`,
    `Twice of ${answer}`,
    `Half of ${answer}`,
    'Cannot be determined from the given data',
  ];
};

const ensureDraftHasMcqOptions = (draft: QuestionBulkDraft): QuestionBulkDraft => {
  if (!draft.isMultipleChoice) {
    return draft;
  }

  const existingOptions = [draft.optionA, draft.optionB, draft.optionC, draft.optionD, draft.optionE]
    .map((option) => normalizeText(option))
    .filter((option) => option.length > 0);

  const uniqueOptions: string[] = [];
  existingOptions.forEach((option) => {
    if (!uniqueOptions.some((item) => item.toLowerCase() === option.toLowerCase())) {
      uniqueOptions.push(option);
    }
  });

  const defaultOptions = generateDefaultOptionsFromAnswer(draft.answerText);
  defaultOptions.forEach((option) => {
    if (!uniqueOptions.some((item) => item.toLowerCase() === option.toLowerCase())) {
      uniqueOptions.push(option);
    }
  });

  while (uniqueOptions.length < 5) {
    uniqueOptions.push(`Choice ${String.fromCharCode(65 + uniqueOptions.length)}`);
  }

  const finalOptions = uniqueOptions.slice(0, 5);
  const normalizedAnswer = normalizeText(draft.answerText);
  let normalizedCorrect = normalizeCorrectOption(draft.correctOption);

  if (!normalizedCorrect && normalizedAnswer) {
    const answerIndex = finalOptions.findIndex((option) => option.toLowerCase() === normalizedAnswer.toLowerCase());
    if (answerIndex >= 0) {
      normalizedCorrect = ['A', 'B', 'C', 'D', 'E'][answerIndex];
    }
  }

  const hasEnoughOriginalOptions = existingOptions.length >= 2;

  return {
    ...draft,
    optionA: finalOptions[0],
    optionB: finalOptions[1],
    optionC: finalOptions[2],
    optionD: finalOptions[3],
    optionE: finalOptions[4],
    correctOption: normalizedCorrect || 'A',
    optionsAutoGenerated: !hasEnoughOriginalOptions,
  };
};

const prepareForMathJax = (value?: string | null): string => {
  const text = normalizeText(value);
  if (!text) return '';

  const hasDelimiters = /\$|\\\(|\\\[/.test(text);
  const looksLikeLatex = /\\[a-zA-Z]+|\^|_/.test(text);

  if (!hasDelimiters && looksLikeLatex) {
    return `\\(${text}\\)`;
  }

  return text;
};

const toHtmlMarkup = (value?: string | null): string => {
  const prepared = prepareForMathJax(value);
  if (!prepared) return '';

  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(prepared);
  if (hasHtmlTags) {
    return prepared;
  }

  return prepared
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br/>');
};

const isMultipleChoiceValid = (formData: QuestionFormData): boolean => {
  if (!formData.isMultipleChoice) {
    return true;
  }

  const options = [formData.optionA, formData.optionB, formData.optionC, formData.optionD, formData.optionE]
    .map((option) => option.trim())
    .filter((option) => option.length > 0);

  return options.length >= 2 && ['A', 'B', 'C', 'D', 'E'].includes(formData.correctOption.trim().toUpperCase());
};

const ManageQuestions: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [organizationContext, setOrganizationContextState] = useState(() => getOrganizationContext());
  const [organizations, setOrganizations] = useState<UserOrganizationItem[]>([]);
  const [courseLeafOptions, setCourseLeafOptions] = useState<CourseLeafOption[]>([]);
  const [courseLeafOptionsLoaded, setCourseLeafOptionsLoaded] = useState(false);
  const [courseFilter, setCourseFilter] = useState('');
  const [editorCourseFilter, setEditorCourseFilter] = useState('all');
  const [editorCategories, setEditorCategories] = useState<CategoryLookupItem[]>([]);
  const [editorLoadingCategories, setEditorLoadingCategories] = useState(false);
  const [isCourseSwitching, setIsCourseSwitching] = useState(false);

  const [categories, setCategories] = useState<CategoryLookupItem[]>([]);
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [difficultyFilter, setDifficultyFilter] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending-review' | 'approved' | 'rejected'>('all');
  const [searchText, setSearchText] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [hasLoadedCategoriesOnce, setHasLoadedCategoriesOnce] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>(buildEmptyForm());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalMode, setAiModalMode] = useState<'single' | 'bulk'>('single');
  const [bulkReviewOpen, setBulkReviewOpen] = useState(false);
  const [bulkQuestionDrafts, setBulkQuestionDrafts] = useState<QuestionBulkDraft[]>([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [createdQuestionIds, setCreatedQuestionIds] = useState<number[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [bulkStatusIsActive, setBulkStatusIsActive] = useState<'no-change' | 'active' | 'inactive'>('no-change');
  const [bulkStatusApproval, setBulkStatusApproval] = useState<'no-change' | 'pending-review' | 'approved' | 'rejected'>('no-change');
  const [bulkStatusSaving, setBulkStatusSaving] = useState(false);

  const parseCourseFilterToId = useCallback((value: string): number | null => {
    if (!value.startsWith('course:')) {
      return null;
    }

    const nextCourseId = Number(value.split(':')[1]);
    return Number.isFinite(nextCourseId) ? nextCourseId : null;
  }, []);

  const selectedCourseId = useMemo(() => parseCourseFilterToId(courseFilter), [courseFilter, parseCourseFilterToId]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => {
      const firstOrder = a.sortOrder && a.sortOrder > 0 ? a.sortOrder : 1;
      const secondOrder = b.sortOrder && b.sortOrder > 0 ? b.sortOrder : 1;
      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }
      const firstLabel = (a.label || a.name || '').trim();
      const secondLabel = (b.label || b.name || '').trim();
      return firstLabel.localeCompare(secondLabel);
    }),
    [categories]
  );

  const sortedEditorCategories = useMemo(
    () => [...editorCategories].sort((a, b) => {
      const firstOrder = a.sortOrder && a.sortOrder > 0 ? a.sortOrder : 1;
      const secondOrder = b.sortOrder && b.sortOrder > 0 ? b.sortOrder : 1;
      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }
      const firstLabel = (a.label || a.name || '').trim();
      const secondLabel = (b.label || b.name || '').trim();
      return firstLabel.localeCompare(secondLabel);
    }),
    [editorCategories]
  );

  const categoryNameMap = useMemo(() => {
    return categories.reduce<Record<number, string>>((map, category) => {
      map[category.id] = category.name;
      return map;
    }, {});
  }, [categories]);

  const filteredQuestions = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return questions.filter((question) => {
      if (search) {
        const haystack = [
          question.questionText,
          question.questionPrefix,
          question.answerText,
          question.hint,
          question.source,
          question.correctOption
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (difficultyFilter && question.difficultyLevel !== difficultyFilter) {
        return false;
      }

      return true;
    });
  }, [difficultyFilter, questions, searchText]);

  const selectedCategory = useMemo(
    () => sortedCategories.find((category) => category.id === categoryFilter) || null,
    [categoryFilter, sortedCategories]
  );

  const bulkAiContext = useMemo(() => {
    if (!selectedCategory) {
      return '';
    }

    return [
      `Category: ${selectedCategory.name}`,
      selectedCategory.parentCategoryId ? `Parent Category Id: ${selectedCategory.parentCategoryId}` : '',
      selectedCategory.releaseVersion ? `Release Version: ${selectedCategory.releaseVersion}` : '',
      'Generate full question records with question text, answer text, explanation, hint, difficulty, options, correct option, and metadata.',
      'Default every draft to approvalStatus = Pending Review and isActive = false.'
    ].filter(Boolean).join('\n');
  }, [selectedCategory]);

  const getAuthToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const loadOrganizations = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get<UserOrganizationItem[]>(`${API_BASE_URL}/organizations/my`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const list = Array.isArray(response.data) ? response.data : [];
      setOrganizations(list);

      if (!organizationContext && list.length > 0) {
        const fallback = list.find((org) => org.isDefault) ?? list[0];
        const nextContext = {
          organizationId: fallback.organizationId,
          organizationSlug: fallback.organizationSlug,
          organizationName: fallback.organizationName
        };
        setOrganizationContext(nextContext);
        setOrganizationContextState(nextContext);
      }
    } catch (loadError) {
      console.error('Failed to load organizations:', loadError);
    }
  }, [getAuthToken, organizationContext]);

  const loadQuestions = useCallback(async (categoryId: number | null, difficulty: number | null, activeState: 'all' | 'active' | 'inactive', approvalState: 'all' | 'pending-review' | 'approved' | 'rejected') => {
    if (!categoryId) {
      setQuestions([]);
      return;
    }

    try {
      setLoadingQuestions(true);
      const params = new URLSearchParams();
      params.set('categoryId', String(categoryId));
      if (difficulty) {
        params.set('difficultyLevel', String(difficulty));
      }
      if (activeState === 'active') {
        params.set('isActive', 'true');
      } else if (activeState === 'inactive') {
        params.set('isActive', 'false');
      }

      if (approvalState !== 'all') {
        params.set('approvalStatus', approvalState === 'pending-review' ? 'Pending Review' : approvalState === 'approved' ? 'Approved' : 'Rejected');
      }

      if (!hasActiveOrganization) {
        setQuestions([]);
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<QuestionItem[]>(`${API_BASE_URL}/questions?${params.toString()}`, {
        headers: getOrganizationHeaders(token)
      });
      setQuestions(response.data || []);
    } catch (loadError) {
      console.error('Failed to load questions:', loadError);
      setError('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      if (!hasActiveOrganization) {
        setCategories([]);
        setCategoryFilter(null);
        setFormData((previous) => ({ ...previous, categoryId: null }));
        setError('No organization selected. Please choose an organization first.');
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<CategoryLookupItem[]>(`${API_BASE_URL}/categories/lookup/leaf`, {
        headers: getOrganizationHeaders(token),
        params: {
          courseId: selectedCourseId ?? undefined,
        },
      });
      const loadedCategories = Array.isArray(response.data) ? response.data : [];
      setCategories(loadedCategories);
      setCategoryFilter((previous) => {
        if (previous && loadedCategories.some((category) => category.id === previous)) {
          return previous;
        }

        return loadedCategories[0]?.id ?? null;
      });
      setFormData((previous) => {
        if (previous.categoryId !== null && loadedCategories.some((category) => category.id === previous.categoryId)) {
          return previous;
        }

        return { ...previous, categoryId: loadedCategories[0]?.id ?? null };
      });
    } catch (loadError) {
      console.error('Failed to load categories:', loadError);
      setError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
      setIsCourseSwitching(false);
      setHasLoadedCategoriesOnce(true);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, selectedCourseId]);

  const loadCourseLeafOptions = useCallback(async () => {
    try {
      if (!hasActiveOrganization || courseLeafOptionsLoaded) {
        return;
      }

      const token = await getManagementToken();
      const response = await axios.get<CourseLeafOption[]>(`${API_BASE_URL}/courses/leaf-options`, {
        headers: getOrganizationHeaders(token),
      });

      const loadedCourses = Array.isArray(response.data) ? response.data : [];
      setCourseLeafOptions(loadedCourses);
      setCourseFilter((previous) => {
        if (previous) {
          return previous;
        }

        return loadedCourses[0] ? `course:${loadedCourses[0].id}` : 'all';
      });
      setCourseLeafOptionsLoaded(true);
    } catch (loadError) {
      console.error('Failed to load course leaf options:', loadError);
      setCourseLeafOptions([]);
      setCourseFilter((previous) => previous || 'all');
      setCourseLeafOptionsLoaded(true);
    }
  }, [API_BASE_URL, courseLeafOptionsLoaded, getManagementToken, getOrganizationHeaders, hasActiveOrganization]);

  const loadEditorCategories = useCallback(async (courseFilterValue: string, preferredCategoryId: number | null = null) => {
    if (!hasActiveOrganization) {
      setEditorCategories([]);
      setFormData((previous) => ({ ...previous, categoryId: null }));
      return;
    }

    try {
      setEditorLoadingCategories(true);
      const token = await getManagementToken();
      const response = await axios.get<CategoryLookupItem[]>(`${API_BASE_URL}/categories/lookup/leaf`, {
        headers: getOrganizationHeaders(token),
        params: {
          courseId: parseCourseFilterToId(courseFilterValue) ?? undefined,
        },
      });

      const loaded = Array.isArray(response.data) ? response.data : [];
      setEditorCategories(loaded);
      setFormData((previous) => {
        const keepPreferred = preferredCategoryId !== null && loaded.some((category) => category.id === preferredCategoryId)
          ? preferredCategoryId
          : null;
        const keepCurrent = previous.categoryId !== null && loaded.some((category) => category.id === previous.categoryId)
          ? previous.categoryId
          : null;
        return { ...previous, categoryId: keepPreferred ?? keepCurrent ?? loaded[0]?.id ?? null };
      });
    } catch (error) {
      console.error('Failed to load editor categories:', error);
      setEditorCategories([]);
      setFormData((previous) => ({ ...previous, categoryId: null }));
    } finally {
      setEditorLoadingCategories(false);
    }
  }, [API_BASE_URL, getManagementToken, getOrganizationHeaders, hasActiveOrganization, parseCourseFilterToId]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    void loadCourseLeafOptions();
  }, [loadCourseLeafOptions]);

  useEffect(() => {
    if (!hasActiveOrganization) {
      void loadCategories();
      return;
    }

    if (!courseLeafOptionsLoaded || !courseFilter) {
      return;
    }

    void loadCategories();
  }, [courseFilter, courseLeafOptionsLoaded, hasActiveOrganization, loadCategories]);

  useEffect(() => {
    if (!categoryFilter) {
      setQuestions([]);
      return;
    }

    loadQuestions(categoryFilter, difficultyFilter, activeFilter, approvalFilter);
  }, [activeFilter, approvalFilter, categoryFilter, difficultyFilter, loadQuestions]);

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }

    if (showForm) {
      return;
    }

    if (formData.categoryId === null || !sortedCategories.some((category) => category.id === formData.categoryId)) {
      setFormData((previous) => ({ ...previous, categoryId: selectedCategory.id }));
    }
  }, [formData.categoryId, selectedCategory, showForm, sortedCategories]);

  useEffect(() => {
    const existingIds = new Set(questions.map((question) => question.id));
    setSelectedQuestionIds((previous) => previous.filter((id) => existingIds.has(id)));
  }, [questions]);

  useEffect(() => {
    if (!showForm) {
      return;
    }

    blurActiveElement();
  }, [showForm]);

  useEffect(() => {
    if (!showForm) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showForm]);

  const resetForm = useCallback((categoryId: number | null = categoryFilter) => {
    setEditingQuestionId(null);
    setShowForm(false);
    setFormData(buildEmptyForm(categoryId ?? categories[0]?.id ?? null));
  }, [categories, categoryFilter]);

  const beginCreate = useCallback(() => {
    blurActiveElement();
    setEditingQuestionId(null);
    const initialEditorCourse = courseFilter || 'all';
    setEditorCourseFilter(initialEditorCourse);
    setShowForm(true);
    setError(null);
    setMessage(null);
    const initialCategoryId = categoryFilter ?? categories[0]?.id ?? null;
    setFormData(buildEmptyForm(initialCategoryId));
    void loadEditorCategories(initialEditorCourse, initialCategoryId);
  }, [categories, categoryFilter, courseFilter, loadEditorCategories]);

  const selectCategory = (categoryId: number | null) => {
    setCategoryFilter(categoryId);
    setEditingQuestionId(null);
    setMessage(null);
    setError(null);
    setFormData((previous) => ({
      ...previous,
      categoryId: categoryId ?? previous.categoryId
    }));
  };

  const handleEditorCourseChange = (value: string) => {
    if (value === editorCourseFilter) {
      return;
    }

    setEditorCourseFilter(value);
    setFormData((previous) => ({ ...previous, categoryId: null }));
    void loadEditorCategories(value, null);
  };

  const handleOrganizationChange = async (organizationId: number) => {
    const organization = organizations.find((item) => item.organizationId === organizationId);
    if (!organization) {
      return;
    }

    const nextContext = {
      organizationId: organization.organizationId,
      organizationSlug: organization.organizationSlug,
      organizationName: organization.organizationName
    };

    setOrganizationContext(nextContext);
    setOrganizationContextState(nextContext);
    setCourseLeafOptions([]);
    setCourseLeafOptionsLoaded(false);
    setCourseFilter('');
    setCategoryFilter(null);
    setQuestions([]);
    resetForm(null);
  };

  const handleEdit = (question: QuestionItem) => {
    blurActiveElement();
    const initialEditorCourse = 'all';
    setEditorCourseFilter(initialEditorCourse);
    setEditingQuestionId(question.id);
    setShowForm(true);
    setMessage(null);
    setError(null);
    setCategoryFilter(question.categoryId);
    setFormData({
      categoryId: question.categoryId,
      approvalStatus: question.approvalStatus || 'Approved',
      questionText: question.questionText || '',
      questionPrefix: question.questionPrefix || '',
      answerText: question.answerText || '',
      explanation: question.explanation || '',
      answerExplanation: question.answerExplanation || '',
      answerExplanationJson: question.answerExplanationJson || '',
      hint: question.hint || '',
      difficultyLevel: question.difficultyLevel || 1,
      isActive: question.isActive,
      source: question.source || '',
      imageUrl: question.imageUrl || '',
      optionA: question.optionA || '',
      optionB: question.optionB || '',
      optionC: question.optionC || '',
      optionD: question.optionD || '',
      optionE: question.optionE || '',
      correctOption: question.correctOption || '',
      isMultipleChoice: question.isMultipleChoice,
      questionLatex: question.questionLatex || '',
      answerLatex: question.answerLatex || '',
      graphLatex: question.graphLatex || '',
      questionForOpenAI: question.questionForOpenAI || '',
      promptKey: question.promptKey || '',
      promptVersion: question.promptVersion || '',
      promptModel: question.promptModel || '',
      promptResponseMode: question.promptResponseMode || ''
    });
    void loadEditorCategories(initialEditorCourse, question.categoryId);
  };

  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsUploadingImage(true);
      setError(null);
      setMessage(null);

      const token = await getManagementToken();
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('assetType', 'question');
      uploadFormData.append('entityType', 'question');

      const response = await fetch(`${API_BASE_URL}/assets/upload`, {
        method: 'POST',
        headers: getOrganizationHeaders(token),
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || 'Failed to upload image');
      }

      const data = await response.json();
      setFormData((previous) => ({ ...previous, imageUrl: data.url || '' }));
      setMessage('Image uploaded successfully.');
    } catch (uploadError: any) {
      console.error('Failed to upload image:', uploadError);
      setError(uploadError?.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      event.target.value = '';
    }
  }, [getManagementToken, getOrganizationHeaders]);

  const handleDelete = async (questionId: number) => {
    if (!window.confirm('Delete this question?')) {
      return;
    }

    try {
      setSaving(true);
      const token = await getManagementToken();
      await axios.delete(`${API_BASE_URL}/questions/${questionId}`, {
        headers: getOrganizationHeaders(token)
      });

      if (editingQuestionId === questionId) {
        resetForm(categoryFilter);
      }

      if (categoryFilter) {
        await loadQuestions(categoryFilter, difficultyFilter, activeFilter, approvalFilter);
      }
      setMessage('Question deleted successfully.');
    } catch (deleteError: any) {
      console.error('Failed to delete question:', deleteError);
      setError(deleteError.response?.data?.message || 'Failed to delete question');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const categoryId = formData.categoryId;
      if (!categoryId) {
        setError('Category is required');
        return;
      }

      if (!normalizeText(formData.questionText)) {
        setError('Question text is required');
        return;
      }

      if (formData.difficultyLevel < 1 || formData.difficultyLevel > 3) {
        setError('Difficulty must be 1, 2, or 3');
        return;
      }

      if (!isMultipleChoiceValid(formData)) {
        setError('Multiple-choice questions require at least two options and a valid correct option (A-E).');
        return;
      }

      const payload = {
        categoryId,
        approvalStatus: formData.approvalStatus,
        questionText: formData.questionText,
        questionPrefix: formData.questionPrefix,
        answerText: formData.answerText,
        explanation: formData.explanation,
        answerExplanation: formData.answerExplanation,
        answerExplanationJson: formData.answerExplanationJson,
        hint: formData.hint,
        difficultyLevel: formData.difficultyLevel,
        isActive: formData.isActive,
        source: formData.source,
        imageUrl: formData.imageUrl,
        optionA: formData.optionA,
        optionB: formData.optionB,
        optionC: formData.optionC,
        optionD: formData.optionD,
        optionE: formData.optionE,
        correctOption: formData.correctOption,
        isMultipleChoice: formData.isMultipleChoice,
        questionLatex: formData.questionLatex,
        answerLatex: formData.answerLatex,
        graphLatex: formData.graphLatex,
        questionForOpenAI: formData.questionForOpenAI,
        promptKey: formData.promptKey,
        promptVersion: formData.promptVersion,
        promptModel: formData.promptModel,
        promptResponseMode: formData.promptResponseMode
      };

      const token = await getManagementToken();
      if (editingQuestionId) {
        await axios.put(`${API_BASE_URL}/questions/${editingQuestionId}`, payload, {
          headers: getOrganizationHeaders(token)
        });
        setMessage('Question updated successfully.');
      } else {
        await axios.post(`${API_BASE_URL}/questions`, payload, {
          headers: getOrganizationHeaders(token)
        });
        setMessage('Question created successfully.');
      }

      const nextCategoryId = categoryId;
      setCategoryFilter(nextCategoryId);
      await loadQuestions(nextCategoryId, difficultyFilter, activeFilter, approvalFilter);
      resetForm(nextCategoryId);
    } catch (saveError: any) {
      console.error('Failed to save question:', saveError);
      setError(saveError.response?.data?.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleStartBulkGeneration = () => {
    if (!selectedCategory) {
      setError('Select a category before generating questions in bulk.');
      return;
    }

    setAiModalMode('bulk');
    setAiModalOpen(true);
  };

  const handleBulkAiApply = (generatedText: string, metadata?: BulkQuestionGenerationMeta) => {
    try {
      const drafts = parseBulkQuestionDrafts(generatedText, metadata);
      if (drafts.length === 0) {
        setError('The AI response did not contain any question drafts.');
        return;
      }

      const selectedCategoryId = selectedCategory?.id ?? categoryFilter;
      const normalizedDrafts = drafts.map((draft) => ({
        ...draft,
        categoryId: draft.categoryId || (selectedCategoryId || 0),
        approvalStatus: normalizeApprovalStatus(draft.approvalStatus),
        isActive: false,
      }));

      setBulkQuestionDrafts(normalizedDrafts);
      setBulkReviewOpen(true);
      setMessage(`Prepared ${normalizedDrafts.length} question drafts for review.`);
    } catch (parseError) {
      console.error('Failed to parse bulk AI questions:', parseError);
      setError('The AI response was not valid JSON. Please try again.');
    }
  };

  const updateBulkDraft = (draftId: number, updates: Partial<QuestionBulkDraft>) => {
    setBulkQuestionDrafts((previous) =>
      previous.map((draft) =>
        draft.id === draftId
          ? {
              ...draft,
              ...updates,
            }
          : draft
      )
    );
  };

  const handleNormalizeBulkDrafts = () => {
    setBulkQuestionDrafts((previous) => previous.map(sanitizeBulkDraft));
    setMessage('Drafts normalized. Review highlighted items before creating.');
  };

  const handleBulkCreate = async () => {
    try {
      setBulkSaving(true);
      setError(null);
      setMessage(null);

      if (!bulkQuestionDrafts.length) {
        setError('No question drafts are available to create.');
        return;
      }

      const sanitizedDrafts = bulkQuestionDrafts.map(sanitizeBulkDraft);
      const invalidDrafts = sanitizedDrafts
        .map((draft) => ({ draft, reason: getBulkDraftValidationError(draft) }))
        .filter((item) => !!item.reason);

      if (invalidDrafts.length > 0) {
        const details = invalidDrafts
          .slice(0, 5)
          .map((item) => `Draft ${item.draft.id}: ${item.reason}`)
          .join(' ');
        const suffix = invalidDrafts.length > 5 ? ` (+${invalidDrafts.length - 5} more)` : '';
        setError(`Cannot create questions because ${invalidDrafts.length} draft(s) are invalid. ${details}${suffix}`);
        setBulkQuestionDrafts(sanitizedDrafts);
        return;
      }

      const payload = sanitizedDrafts.map((draft) => ({
        categoryId: draft.categoryId,
        approvalStatus: 'Pending Review',
        questionText: draft.questionText,
        questionPrefix: draft.questionPrefix,
        answerText: draft.answerText,
        explanation: draft.explanation,
        answerExplanation: draft.answerExplanation,
        answerExplanationJson: draft.answerExplanationJson,
        hint: draft.hint,
        difficultyLevel: draft.difficultyLevel,
        isActive: false,
        source: draft.source,
        imageUrl: draft.imageUrl,
        optionA: draft.optionA,
        optionB: draft.optionB,
        optionC: draft.optionC,
        optionD: draft.optionD,
        optionE: draft.optionE,
        correctOption: draft.correctOption,
        isMultipleChoice: draft.isMultipleChoice,
        questionLatex: draft.questionLatex,
        answerLatex: draft.answerLatex,
        graphLatex: draft.graphLatex,
        questionForOpenAI: draft.questionForOpenAI,
        promptKey: draft.promptKey,
        promptVersion: draft.promptVersion,
        promptModel: draft.promptModel,
        promptResponseMode: draft.promptResponseMode,
      }));

      const token = await getManagementToken();
      const response = await axios.post(`${API_BASE_URL}/questions/bulk`, payload, {
        headers: getOrganizationHeaders(token)
      });

      const createdQuestions = Array.isArray(response.data) ? response.data : [];
      const createdIds = createdQuestions.map((question: QuestionItem) => question.id).filter(Boolean);
      setCreatedQuestionIds(createdIds);
      setBulkReviewOpen(false);
      setBulkQuestionDrafts([]);
      setMessage(`Created ${createdQuestions.length} questions as Pending Review.`);

      if (selectedCategory?.id) {
        setCategoryFilter(selectedCategory.id);
        await loadQuestions(selectedCategory.id, difficultyFilter, activeFilter, approvalFilter);
      }
    } catch (bulkError: any) {
      console.error('Failed to create bulk questions:', bulkError);
      setError(bulkError.response?.data?.message || 'Failed to create bulk questions');
    } finally {
      setBulkSaving(false);
    }
  };

  const previewList = filteredQuestions;

  const visibleQuestionIds = useMemo(() => previewList.map((question) => question.id), [previewList]);

  const visibleSelectedCount = useMemo(
    () => selectedQuestionIds.filter((id) => visibleQuestionIds.includes(id)).length,
    [selectedQuestionIds, visibleQuestionIds]
  );

  const areAllVisibleSelected = visibleQuestionIds.length > 0 && visibleSelectedCount === visibleQuestionIds.length;

  const toggleQuestionSelection = (questionId: number, checked: boolean) => {
    setSelectedQuestionIds((previous) => {
      if (checked) {
        if (previous.includes(questionId)) {
          return previous;
        }

        return [...previous, questionId];
      }

      return previous.filter((id) => id !== questionId);
    });
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedQuestionIds((previous) => {
      if (checked) {
        const merged = new Set([...previous, ...visibleQuestionIds]);
        return Array.from(merged);
      }

      const visibleSet = new Set(visibleQuestionIds);
      return previous.filter((id) => !visibleSet.has(id));
    });
  };

  const clearBulkSelection = () => {
    setSelectedQuestionIds([]);
  };

  const handleBulkStatusApply = async () => {
    try {
      setBulkStatusSaving(true);
      setError(null);
      setMessage(null);

      if (selectedQuestionIds.length === 0) {
        setError('Select at least one question to apply bulk update.');
        return;
      }

      if (bulkStatusIsActive === 'no-change' && bulkStatusApproval === 'no-change') {
        setError('Choose at least one field to update: status or review status.');
        return;
      }

      const payload = {
        questionIds: selectedQuestionIds,
        isActive: bulkStatusIsActive === 'no-change' ? null : bulkStatusIsActive === 'active',
        approvalStatus: bulkStatusApproval === 'no-change'
          ? null
          : bulkStatusApproval === 'pending-review'
            ? 'Pending Review'
            : bulkStatusApproval === 'approved'
              ? 'Approved'
              : 'Rejected',
      };

      const token = await getManagementToken();
      const response = await axios.post<BulkStatusUpdateResponse>(`${API_BASE_URL}/questions/bulk-status`, payload, {
        headers: getOrganizationHeaders(token),
      });

      const data = response.data;
      const skippedSuffix = data.skippedIds && data.skippedIds.length > 0
        ? ` ${data.skippedIds.length} item(s) were skipped.`
        : '';

      setMessage(`Updated ${data.updatedCount} question(s).${skippedSuffix}`);
      setBulkStatusIsActive('no-change');
      setBulkStatusApproval('no-change');
      clearBulkSelection();

      if (categoryFilter) {
        await loadQuestions(categoryFilter, difficultyFilter, activeFilter, approvalFilter);
      }
    } catch (bulkStatusError: any) {
      console.error('Failed to bulk update question status:', bulkStatusError);
      setError(bulkStatusError.response?.data?.message || 'Failed to bulk update question status');
    } finally {
      setBulkStatusSaving(false);
    }
  };

  const filterConfigs = useMemo(() => [
    {
      key: 'course',
      label: 'Course',
      value: courseFilter,
      onChange: (value: string) => {
        if (value === courseFilter) {
          return;
        }

        setIsCourseSwitching(true);
        setCourseFilter(value);
        setCategoryFilter(null);
        setSelectedQuestionIds([]);
        setFormData((previous) => ({ ...previous, categoryId: null }));
        setShowForm(false);
        setEditingQuestionId(null);
        setMessage(null);
        setQuestions([]);
      },
      options: [
        { value: 'all', label: 'All Courses' },
        ...courseLeafOptions.map((course) => ({ value: `course:${course.id}`, label: course.pathLabel || course.name })),
      ],
    },
    {
      key: 'category',
      label: 'Category',
      value: categoryFilter ? String(categoryFilter) : '',
      onChange: (value: string) => selectCategory(value ? Number(value) : null),
      options: [
        { value: '', label: 'All Categories' },
        ...sortedCategories.map((category) => ({ value: String(category.id), label: category.label || category.name })),
      ],
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      value: difficultyFilter ? String(difficultyFilter) : '',
      onChange: (value: string) => setDifficultyFilter(value ? Number(value) : null),
      options: [
        { value: '', label: 'All Difficulties' },
        { value: '1', label: 'Easy' },
        { value: '2', label: 'Medium' },
        { value: '3', label: 'Hard' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      value: activeFilter,
      onChange: (value: string) => setActiveFilter(value as 'all' | 'active' | 'inactive'),
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'all', label: 'All Status' },
      ],
    },
    {
      key: 'approval',
      label: 'Review',
      value: approvalFilter,
      onChange: (value: string) => setApprovalFilter(value as 'all' | 'pending-review' | 'approved' | 'rejected'),
      options: [
        { value: 'all', label: 'All Review States' },
        { value: 'pending-review', label: 'Pending Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
      ],
    },
  ], [activeFilter, approvalFilter, categoryFilter, courseFilter, courseLeafOptions, difficultyFilter, selectCategory, sortedCategories]);

  if (loadingCategories && !hasLoadedCategoriesOnce) {
    return <div style={{ padding: 24, fontSize: 18 }}>Loading questions...</div>;
  }

  return (
    <MathJaxContext>
      <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
        <AdminBreadcrumbs
          items={[
            { label: 'Administration', to: '/analytics' },
            { label: 'Manage Questions', isCurrent: true },
          ]}
        />
        <AdminPageHeader
          title="Manage Questions"
          subtitle="Create, edit, delete, and preview questions by category."
          actions={[
            {
              key: 'new-question',
              label: 'New Question',
              variant: 'primary',
              onClick: () => beginCreate(),
            },
            {
              key: 'bulk-ai-generate-questions',
              label: 'Bulk AI Generate Questions',
              variant: 'secondary',
              onClick: handleStartBulkGeneration,
            },
          ]}
        />

        {error && (
          <div style={{ marginBottom: 16, backgroundColor: '#ffebee', color: '#c62828', padding: 12, borderRadius: 6 }}>
            {error}
            <button
              onClick={() => setError(null)}
              style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}
            >
              ×
            </button>
          </div>
        )}

        {message && (
          <div style={{ marginBottom: 16, backgroundColor: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 6 }}>
            {message}
            <button
              onClick={() => setMessage(null)}
              style={{ float: 'right', border: 'none', background: 'transparent', cursor: 'pointer', color: '#2e7d32' }}
            >
              ×
            </button>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr',
            gap: 16,
            alignItems: 'start',
            pointerEvents: isCourseSwitching ? 'none' : 'auto',
            opacity: isCourseSwitching ? 0.72 : 1,
            transition: 'opacity 120ms ease',
          }}
          aria-busy={isCourseSwitching}
        >
          <div
            style={{
              border: '1px solid #dfe4ee',
              borderRadius: 10,
              backgroundColor: '#fff',
              padding: 14,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              display: showForm ? 'none' : 'block',
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#222' }}>Question List</h3>

            <div style={{ marginBottom: 12 }}>
              <AdminFilterBar
                searchValue={searchText}
                onSearchChange={setSearchText}
                searchPlaceholder="Search question text, source, answer..."
                filters={filterConfigs}
              />
            </div>

            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              {selectedCategory ? `Category: ${selectedCategory.name}` : 'Choose a category to load questions.'}
            </div>

            {loadingCategories && hasLoadedCategoriesOnce && (
              <div style={{ fontSize: 12, color: '#667085', marginBottom: 8 }}>
                {isCourseSwitching ? 'Switching course and refreshing categories...' : 'Updating categories for selected course...'}
              </div>
            )}

            {categoryFilter && (
              <div style={{ marginBottom: 12, border: '1px solid #dbe4f0', borderRadius: 8, padding: 10, backgroundColor: '#f8fbff', display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#344054' }}>
                    <input
                      type="checkbox"
                      checked={areAllVisibleSelected}
                      onChange={(event) => toggleSelectAllVisible(event.target.checked)}
                    />
                    Select all visible
                  </label>
                  <div style={{ fontSize: 12, color: '#667085' }}>
                    Selected: {selectedQuestionIds.length}
                  </div>
                </div>

                {selectedQuestionIds.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#475467' }}>Set Status</label>
                      <select
                        value={bulkStatusIsActive}
                        onChange={(event) => setBulkStatusIsActive(event.target.value as 'no-change' | 'active' | 'inactive')}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1' }}
                      >
                        <option value="no-change">No change</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#475467' }}>Set Review Status</label>
                      <select
                        value={bulkStatusApproval}
                        onChange={(event) => setBulkStatusApproval(event.target.value as 'no-change' | 'pending-review' | 'approved' | 'rejected')}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1' }}
                      >
                        <option value="no-change">No change</option>
                        <option value="pending-review">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      className="amd-btn amd-btn-secondary"
                      onClick={clearBulkSelection}
                      disabled={bulkStatusSaving}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Clear Selection
                    </button>

                    <button
                      type="button"
                      className="amd-btn amd-btn-primary"
                      onClick={handleBulkStatusApply}
                      disabled={bulkStatusSaving}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {bulkStatusSaving ? 'Updating...' : 'Bulk Update'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 280, overflowY: 'auto', display: 'grid', gap: 10 }}>
              {!categoryFilter ? (
                <div style={{ color: '#777' }}>Select a category to load questions.</div>
              ) : loadingQuestions ? (
                <div style={{ color: '#777' }}>Loading questions...</div>
              ) : previewList.length === 0 ? (
                <div style={{ color: '#777' }}>No questions found for the selected filters.</div>
              ) : (
                previewList.map((question) => {
                  const isEditing = editingQuestionId === question.id;
                  const isSelected = selectedQuestionIds.includes(question.id);
                  const options = [
                    { label: 'A', value: question.optionA },
                    { label: 'B', value: question.optionB },
                    { label: 'C', value: question.optionC },
                    { label: 'D', value: question.optionD },
                    { label: 'E', value: question.optionE }
                  ].filter((option) => !!option.value && option.value.trim().length > 0);

                  return (
                    <div
                      key={question.id}
                      style={{
                        border: isEditing ? '1px solid #90caf9' : createdQuestionIds.includes(question.id) ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
                        backgroundColor: isEditing ? '#e3f2fd' : isSelected ? '#f5faff' : createdQuestionIds.includes(question.id) ? '#f1f8e9' : '#fff',
                        borderRadius: 8,
                        padding: 12
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475467' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(event) => toggleQuestionSelection(question.id, event.target.checked)}
                          />
                          Select
                        </label>
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                        Q{question.id} • {categoryNameMap[question.categoryId] || `Category ${question.categoryId}`} • Difficulty {question.difficultyLevel} • {question.isActive ? 'Active' : 'Inactive'} • {question.approvalStatus || 'Approved'}
                      </div>
                      <div style={{ color: '#333', marginBottom: 8, lineHeight: 1.4, textAlign: 'left', fontWeight: 700 }}>
                        <MathJax dynamic>
                          <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(question.questionText) }} />
                        </MathJax>
                        {question.imageUrl && (
                          <div style={{ marginTop: 8 }}>
                            <img
                              src={question.imageUrl}
                              alt="Question illustration"
                              style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid #e0e0e0' }}
                            />
                          </div>
                        )}
                      </div>

                      {options.length > 0 && (
                        <div style={{ marginBottom: 8, fontSize: 12, color: '#444', display: 'grid', gap: 3 }}>
                          {options.map((option) => (
                            <div key={`${question.id}-${option.label}`} style={{ textAlign: 'left' }}>
                              <strong>{option.label}.</strong>{' '}
                              <span style={{ display: 'inline-block' }}>
                                <MathJax inline dynamic>
                                  <span dangerouslySetInnerHTML={{ __html: toHtmlMarkup(option.value) }} />
                                </MathJax>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleEdit(question)}
                          className="amd-btn amd-btn-primary"
                          style={{ fontSize: 12, padding: '6px 12px' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(question.id)}
                          disabled={saving}
                          className="amd-btn amd-btn-secondary"
                          style={{ fontSize: 12, padding: '6px 12px', borderColor: '#f9c2c2', color: '#b42318' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div style={{ border: '1px solid #dfe4ee', borderRadius: 10, backgroundColor: '#fff', padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            {!showForm ? (
              <div
                style={{
                  minHeight: 260,
                  border: '1px dashed #c9d6ef',
                  borderRadius: 12,
                  background: '#f8fbff',
                  display: 'grid',
                  placeContent: 'center',
                  textAlign: 'center',
                  color: '#4f658f',
                  gap: 8,
                  padding: 16,
                }}
              >
                <div style={{ fontWeight: 700 }}>Select a question from the list</div>
                <div style={{ fontSize: '0.82rem' }}>or</div>
                <button type="button" className="amd-btn amd-btn-primary" onClick={beginCreate}>Create a new question</button>
              </div>
            ) : (
              <div style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', padding: 20, zIndex: 3000, backgroundColor: 'rgba(15, 23, 42, 0.84)' }}>
              <div style={{ width: 'min(1320px, 96vw)', maxHeight: '92vh', overflow: 'hidden', backgroundColor: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  <h3 style={{ margin: 0, color: '#222' }}>{editingQuestionId ? `Edit Question #${editingQuestionId}` : 'Create Question'}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>New questions are created as active unless you mark them pending review.</div>
                    <button type="button" className="amd-btn amd-btn-secondary" onClick={() => resetForm(categoryFilter)}>Close</button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 0.85fr', gap: 16, alignItems: 'start' }}>
                  <div style={{ maxHeight: '78vh', overflowY: 'auto', display: 'grid', gap: 12, paddingRight: 4 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Course</label>
                  <select
                    value={editorCourseFilter}
                    onChange={(event) => handleEditorCourseChange(event.target.value)}
                    disabled={editorLoadingCategories}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="all">All Courses</option>
                    {courseLeafOptions.map((course) => (
                      <option key={course.id} value={`course:${course.id}`}>{course.pathLabel || course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Category *</label>
                  <select
                    value={formData.categoryId ?? ''}
                    onChange={(event) => setFormData((previous) => ({ ...previous, categoryId: event.target.value ? Number(event.target.value) : null }))}
                    disabled={editorLoadingCategories}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="">Select category</option>
                    {sortedEditorCategories.map((category) => (
                      <option key={category.id} value={category.id}>{category.label || category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Difficulty *</label>
                  <select
                    value={formData.difficultyLevel}
                    onChange={(event) => setFormData((previous) => ({ ...previous, difficultyLevel: Number(event.target.value) }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="1">Easy</option>
                    <option value="2">Medium</option>
                    <option value="3">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Text *</label>
                <textarea
                  value={formData.questionText}
                  onChange={(event) => setFormData((previous) => ({ ...previous, questionText: event.target.value }))}
                  rows={6}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Prefix</label>
                  <input
                    type="text"
                    value={formData.questionPrefix}
                    onChange={(event) => setFormData((previous) => ({ ...previous, questionPrefix: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Source</label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(event) => setFormData((previous) => ({ ...previous, source: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Text</label>
                  <input
                    type="text"
                    value={formData.answerText}
                    onChange={(event) => setFormData((previous) => ({ ...previous, answerText: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Hint</label>
                  <input
                    type="text"
                    value={formData.hint}
                    onChange={(event) => setFormData((previous) => ({ ...previous, hint: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Explanation</label>
                <textarea
                  value={formData.explanation}
                  onChange={(event) => setFormData((previous) => ({ ...previous, explanation: event.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Explanation</label>
                <textarea
                  value={formData.answerExplanation}
                  onChange={(event) => setFormData((previous) => ({ ...previous, answerExplanation: event.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Explanation JSON</label>
                <textarea
                  value={formData.answerExplanationJson}
                  onChange={(event) => setFormData((previous) => ({ ...previous, answerExplanationJson: event.target.value }))}
                  rows={4}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical', fontFamily: 'monospace' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploadingImage}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', backgroundColor: '#fff' }}
                  />
                  <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                    {isUploadingImage ? 'Uploading...' : 'Upload an image to store the URL in the question.'}
                  </div>
                  {formData.imageUrl && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#1976d2', wordBreak: 'break-all' }}>
                      {formData.imageUrl}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question Latex</label>
                  <input
                    type="text"
                    value={formData.questionLatex}
                    onChange={(event) => setFormData((previous) => ({ ...previous, questionLatex: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Answer Latex</label>
                  <input
                    type="text"
                    value={formData.answerLatex}
                    onChange={(event) => setFormData((previous) => ({ ...previous, answerLatex: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Graph Latex</label>
                  <input
                    type="text"
                    value={formData.graphLatex}
                    onChange={(event) => setFormData((previous) => ({ ...previous, graphLatex: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Question For OpenAI</label>
                  <input
                    type="text"
                    value={formData.questionForOpenAI}
                    onChange={(event) => setFormData((previous) => ({ ...previous, questionForOpenAI: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Key</label>
                  <input
                    type="text"
                    value={formData.promptKey}
                    onChange={(event) => setFormData((previous) => ({ ...previous, promptKey: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Version</label>
                  <input
                    type="text"
                    value={formData.promptVersion}
                    onChange={(event) => setFormData((previous) => ({ ...previous, promptVersion: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Model</label>
                  <input
                    type="text"
                    value={formData.promptModel}
                    onChange={(event) => setFormData((previous) => ({ ...previous, promptModel: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Prompt Response Mode</label>
                  <input
                    type="text"
                    value={formData.promptResponseMode}
                    onChange={(event) => setFormData((previous) => ({ ...previous, promptResponseMode: event.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Is Multiple Choice</label>
                  <select
                    value={formData.isMultipleChoice ? 'true' : 'false'}
                    onChange={(event) => setFormData((previous) => ({ ...previous, isMultipleChoice: event.target.value === 'true' }))}
                    style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              {editingQuestionId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(event) => setFormData((previous) => ({ ...previous, isActive: event.target.checked }))}
                  />
                  <span style={{ fontWeight: 600 }}>Active</span>
                </div>
              )}

              {formData.isMultipleChoice && (
                <div style={{ border: '1px solid #eceff4', borderRadius: 8, padding: 12, backgroundColor: '#fafafa' }}>
                  <h4 style={{ marginTop: 0, marginBottom: 10 }}>Multiple Choice Options</h4>
                  <div style={{ display: 'grid', gap: 10 }}>
                    {(['optionA', 'optionB', 'optionC', 'optionD', 'optionE'] as const).map((field, index) => (
                      <div key={field}>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>{String.fromCharCode(65 + index)}</label>
                        <input
                          type="text"
                          value={formData[field]}
                          onChange={(event) => setFormData((previous) => ({ ...previous, [field]: event.target.value }))}
                          style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Correct Option</label>
                    <select
                      value={formData.correctOption}
                      onChange={(event) => setFormData((previous) => ({ ...previous, correctOption: event.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                    >
                      <option value="">Select correct option</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  onClick={() => setAiModalOpen(true)}
                >
                  AI Assist
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    backgroundColor: saving ? '#90caf9' : '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 16px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  {saving ? 'Saving...' : editingQuestionId ? 'Update Question' : 'Create Question'}
                </button>
                <button
                  onClick={() => resetForm(categoryFilter)}
                  style={{
                    backgroundColor: '#757575',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Reset
                </button>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <h4 style={{ marginTop: 0 }}>Preview</h4>
                <div style={{ color: '#333', lineHeight: 1.4 }}>
                  <MathJax dynamic>
                    <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(formData.questionText || 'Question preview will appear here.') }} />
                  </MathJax>
                </div>
              </div>
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, backgroundColor: '#f8fbff', padding: 12, display: 'grid', gap: 10, maxHeight: '78vh', overflowY: 'auto' }}>
                    <h4 style={{ margin: 0, color: '#1f2937' }}>Details Preview</h4>
                    <div style={{ fontSize: 12, color: '#475467', display: 'grid', gap: 4 }}>
                      <div><strong>Category:</strong> {sortedEditorCategories.find((category) => category.id === formData.categoryId)?.label || sortedEditorCategories.find((category) => category.id === formData.categoryId)?.name || sortedCategories.find((category) => category.id === formData.categoryId)?.label || sortedCategories.find((category) => category.id === formData.categoryId)?.name || 'Not selected'}</div>
                      <div><strong>Difficulty:</strong> {formData.difficultyLevel}</div>
                      <div><strong>Type:</strong> {formData.isMultipleChoice ? 'Multiple Choice' : 'Open Ended'}</div>
                      <div><strong>Status:</strong> {formData.isActive ? 'Active' : 'Inactive'}</div>
                      <div><strong>Review:</strong> {formData.approvalStatus || 'Approved'}</div>
                    </div>

                    <div style={{ borderTop: '1px solid #dbe4f0', paddingTop: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Question</div>
                      <MathJax dynamic>
                        <div style={{ color: '#111827', lineHeight: 1.45 }} dangerouslySetInnerHTML={{ __html: toHtmlMarkup(formData.questionText || 'Question preview will appear here.') }} />
                      </MathJax>
                    </div>

                    {formData.imageUrl && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Image</div>
                        <img
                          src={formData.imageUrl}
                          alt="Question preview"
                          style={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 8, border: '1px solid #dbe4f0', backgroundColor: '#fff' }}
                        />
                      </div>
                    )}

                    {formData.isMultipleChoice && (
                      <div style={{ borderTop: '1px solid #dbe4f0', paddingTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Options</div>
                        <div style={{ display: 'grid', gap: 4, fontSize: 12, color: '#111827' }}>
                          {([['A', formData.optionA], ['B', formData.optionB], ['C', formData.optionC], ['D', formData.optionD], ['E', formData.optionE]] as const)
                            .filter(([, value]) => (value || '').trim().length > 0)
                            .map(([label, value]) => (
                              <div key={label}><strong>{label}.</strong> {value}</div>
                            ))}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#1d4ed8' }}>
                          <strong>Correct:</strong> {formData.correctOption || 'Not selected'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AdminAiAssistantModal
        open={aiModalOpen}
        title={aiModalMode === 'bulk' ? 'Bulk Question AI Assistant' : 'Question AI Assistant'}
        promptLabel={aiModalMode === 'bulk' ? 'What should the AI generate?' : 'What would you like the assistant to help with?'}
        promptPlaceholder={aiModalMode === 'bulk' ? 'Example: generate 10 algebra questions with 5 options each.' : 'Example: improve this question wording and give a clearer explanation.'}
        defaultPrompt={aiModalMode === 'bulk' ? 'Generate a JSON array of exam questions for the selected category.' : 'Improve the wording of this question and make the explanation clearer.'}
        contextLabel="Question context"
        contextPlaceholder="Include the current question text, answer, explanation, options, difficulty, or category."
        initialContext={aiModalMode === 'bulk'
          ? bulkAiContext
          : `Question: ${formData.questionText || ''}\nAnswer: ${formData.answerText || ''}\nExplanation: ${formData.explanation || ''}\nOptions: ${[formData.optionA, formData.optionB, formData.optionC, formData.optionD, formData.optionE].filter(Boolean).join(' | ')}`}
        applyLabel={aiModalMode === 'bulk' ? 'Review generated questions' : 'Apply to question text'}
        onClose={() => setAiModalOpen(false)}
        onApply={(generatedText, metadata) => {
          if (aiModalMode === 'bulk') {
            handleBulkAiApply(generatedText, metadata);
            return;
          }

          setFormData((previous) => ({ ...previous, questionText: generatedText }));
          setMessage('AI-generated question text applied.');
        }}
      />

      {bulkReviewOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'grid',
            placeItems: 'center',
            padding: 20,
            zIndex: 4000,
          }}
        >
          <div style={{ width: 'min(1100px, 96vw)', maxHeight: '88vh', overflow: 'auto', backgroundColor: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}>Review Generated Questions</h3>
                <div style={{ color: '#666', fontSize: 13 }}>{bulkQuestionDrafts.length} question drafts ready to create.</div>
              </div>
              <button type="button" onClick={() => setBulkReviewOpen(false)} className="amd-btn amd-btn-secondary">Close</button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {bulkQuestionDrafts.map((draft) => {
                const validationError = getBulkDraftValidationError(draft);
                return (
                <div key={draft.id} style={{ border: validationError ? '1px solid #ef9a9a' : '1px solid #dde5f0', borderRadius: 10, padding: 12, backgroundColor: validationError ? '#fff5f5' : '#fbfcfe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'start', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700 }}>Draft {draft.id}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{draft.approvalStatus} • {draft.isActive ? 'Active' : 'Inactive'}</div>
                  </div>
                  {draft.optionsAutoGenerated && (
                    <div style={{ fontSize: 12, color: '#175cd3', marginBottom: 8, fontWeight: 600 }}>
                      Default options were auto-filled from the answer.
                    </div>
                  )}
                  {validationError && (
                    <div style={{ fontSize: 12, color: '#b42318', marginBottom: 8, fontWeight: 600 }}>
                      {validationError}
                    </div>
                  )}
                  <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5, marginBottom: 8 }}>
                    <MathJax dynamic>
                      <div dangerouslySetInnerHTML={{ __html: toHtmlMarkup(draft.questionText) }} />
                    </MathJax>
                  </div>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#475467' }}>Question Text</label>
                    <textarea
                      value={draft.questionText}
                      onChange={(event) => updateBulkDraft(draft.id, { questionText: event.target.value })}
                      rows={3}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', resize: 'vertical', fontSize: 12 }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 10 }}>
                    {([
                      ['A', 'optionA'],
                      ['B', 'optionB'],
                      ['C', 'optionC'],
                      ['D', 'optionD'],
                      ['E', 'optionE'],
                    ] as const).map(([label, field]) => (
                      <div key={`${draft.id}-${field}`}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>Option {label}</label>
                        <input
                          type="text"
                          value={draft[field]}
                          onChange={(event) => updateBulkDraft(draft.id, { [field]: event.target.value } as Partial<QuestionBulkDraft>)}
                          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'end', marginBottom: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>Correct Option</label>
                      <input
                        type="text"
                        value={draft.correctOption}
                        onChange={(event) => updateBulkDraft(draft.id, { correctOption: event.target.value })}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                        placeholder="A-E"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>Answer Text</label>
                      <input
                        type="text"
                        value={draft.answerText}
                        onChange={(event) => updateBulkDraft(draft.id, { answerText: event.target.value })}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475467', marginBottom: 4 }}>Multiple Choice</label>
                      <select
                        value={draft.isMultipleChoice ? 'true' : 'false'}
                        onChange={(event) => updateBulkDraft(draft.id, { isMultipleChoice: event.target.value === 'true' })}
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, fontSize: 12, color: '#555' }}>
                    <div><strong>Answer:</strong> {draft.answerText || '—'}</div>
                    <div><strong>Difficulty:</strong> {draft.difficultyLevel}</div>
                    <div><strong>Correct:</strong> {draft.correctOption || '—'}</div>
                    <div><strong>Category:</strong> {categoryNameMap[draft.categoryId] || draft.categoryId}</div>
                  </div>
                </div>
              );})}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button type="button" className="amd-btn amd-btn-secondary" onClick={handleNormalizeBulkDrafts} disabled={bulkSaving || bulkQuestionDrafts.length === 0}>Auto Normalize</button>
              <button type="button" className="amd-btn amd-btn-secondary" onClick={() => setBulkReviewOpen(false)} disabled={bulkSaving}>Cancel</button>
              <button type="button" className="amd-btn amd-btn-primary" onClick={handleBulkCreate} disabled={bulkSaving || bulkQuestionDrafts.length === 0}>
                {bulkSaving ? 'Creating...' : 'Create Questions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MathJaxContext>
  );
};

export default ManageQuestions;
